import React from "react";
import { Link } from "react-router-dom";
import { PageHero, SectionHeader, SiteLayout } from "./components/SiteLayout";

const features = [
  { title: "Answers Incoming Calls Any Time", description: "Collentica is available 24/7 — never miss a customer call, even outside business hours." },
  { title: "Real-Time Database Access", description: "Provides live information from your database — inventory levels, pricing, availability, and more." },
  { title: "Books Appointments & Takes Orders", description: "Handles bookings and order collection end-to-end with no human intervention required." },
  { title: "Reduces Missed Calls & Staff Load", description: "Frees up your team from routine calls so they can focus on higher-value interactions." },
  { title: "Natural, Friendly Conversations", description: "Powered by advanced LLMs for human-like dialogue that feels approachable and professional." },
];

const demoSteps = [
  { step: "1", description: "Greets the customer naturally" },
  { step: "2", description: "Asks how it can assist" },
  { step: "3", description: "Understands queries about auto parts" },
  { step: "4", description: "Searches the database in real time" },
  { step: "5", description: "Confirms availability and offers to place order" },
  { step: "6", description: "Collects the customer's name and address" },
  { step: "7", description: "Completes the order and informs a human agent will follow up" },
];

const industries = [
  "Auto parts shops",
  "Medical clinics",
  "Agricultural suppliers",
  "Retail businesses",
  "Fast food and restaurants",
  "Any business that receives customer calls",
];

export default function CallenticaPage() {
  return (
    <SiteLayout title="Callentica – AI Voice Assistant" description="Collentica is SaskTech Lab's AI-powered voice assistant that handles customer calls 24/7 with natural, human-like conversation.">
      {/* Hero */}
      <PageHero
        eyebrow="Callentica – AI Voice Assistant"
        title="The AI Voice Assistant That Never Misses a Call."
        description="Meet Collentica — our AI-powered voice assistant designed to handle customer calls 24/7 with natural, human-like conversation. Whether it's answering questions, checking inventory, or taking orders, Collentica delivers personalized support — anytime, every time."
        actions={[
          <Link key="demo" to="/contact" className="button-tertiary">
            Book a Demo
          </Link>,
        ]}
      />

      {/* Demo callout */}
      <section className="site-shell -mt-4 pb-4">
        <div className="rounded-[28px] border border-emerald-900/10 bg-emerald-50 px-6 py-5 flex flex-wrap gap-6 items-center">
          <div className="flex items-center gap-3 text-sm text-slate-700">
            <span className="text-lg">📞</span>
            <span>
              <span className="font-semibold">Try the Demo:</span>{" "}
              <a href="tel:+15817010001" className="font-bold text-emerald-800 hover:underline">+1 (581) 701-0001</a>
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-700">
            <span className="text-lg">🌐</span>
            <span>
              <span className="font-semibold">Visit Demo Site:</span>{" "}
              <a href="https://reginaautoparts.azurewebsites.net/" target="_blank" rel="noopener noreferrer" className="font-bold text-emerald-800 hover:underline">reginaautoparts.azurewebsites.net</a>
            </span>
          </div>
        </div>
      </section>

      {/* What is Collentica */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="What is Collentica?" title="Intelligent AI voice built for Saskatchewan businesses." description="Collentica is SaskTech Lab's intelligent AI Voice Assistant built for businesses in Saskatchewan. It simulates a real phone conversation using advanced large language models (LLMs), offering a 24/7 automated customer service solution." />
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {features.map((feature) => (
            <article key={feature.title} className="panel-card h-full">
              <h3 className="font-display text-xl font-bold text-emerald-950">{feature.title}</h3>
              <p className="mt-4 text-sm leading-7 text-slate-600">{feature.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Live Demo */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="Live Demo" title="Auto Parts Store Use Case." description="When a user calls the demo number, Collentica handles the entire conversation from greeting to order completion." />
        <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {demoSteps.map((item) => (
            <div key={item.step} className="panel-card flex gap-4 items-start">
              <span className="font-display text-4xl font-bold text-emerald-200 leading-none">{item.step}</span>
              <p className="mt-1 text-sm leading-7 text-slate-600">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works diagram */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="How It Works" title="From incoming call to completed action." description="Collentica connects your phone line to your business data, handling the full customer interaction end-to-end." />
        <div className="mt-10 panel-card p-6 text-center">
          <img
            src="/callentica-how-it-works.png"
            alt="Collentica Call Flow Diagram"
            className="mx-auto max-w-full rounded-2xl"
          />
        </div>
      </section>

      {/* Coming Soon */}
      <section className="site-shell section-space">
        <div className="panel-card">
          <p className="section-tag">Coming Soon</p>
          <h2 className="mt-4 font-display text-3xl font-bold text-emerald-950">Fast Food Drive-Thru Automation</h2>
          <p className="mt-4 text-base leading-8 text-slate-600">
            Our next demo will showcase how Collentica can handle incoming drive-thru orders for fast food shops — fully automated, naturally conversational, and integrated with your menu and order system. Stay tuned!
          </p>
        </div>
      </section>

      {/* Industries & CTA */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="Tailored for Saskatchewan Businesses" title="Built for the businesses that power local communities." description="From auto parts shops and clinics to ag suppliers and retail businesses, Collentica is designed to reduce missed calls, streamline operations, and enhance customer satisfaction — locally." />
        <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {industries.map((industry) => (
            <div key={industry} className="panel-card text-sm font-semibold text-emerald-950">
              {industry}
            </div>
          ))}
        </div>
      </section>

      {/* Get Started CTA */}
      <section className="site-shell section-space pb-10">
        <div className="rounded-[36px] border border-emerald-900/10 bg-gradient-to-br from-white via-emerald-50 to-emerald-100 p-8 lg:p-10">
          <p className="section-tag">Interested in Trying Collentica?</p>
          <h2 className="mt-4 font-display text-3xl font-bold text-emerald-950">Capture more customers with 24/7 AI voice service.</h2>
          <p className="mt-4 max-w-xl text-base text-slate-600">
            Let us help you capture more customers and offer 24/7 service without added staffing costs.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact" className="button-tertiary">Book a Demo</Link>
            <a href="mailto:info@sasktechlab.ca" className="button-secondary">info@sasktechlab.ca</a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
